import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Skull, CalendarDays } from 'lucide-react';
import { bagContents, levelRange } from '../../utils/bagSources';
import type { BagSource } from '../../utils/bagSources';
import { itemPath, monsterName, monsterPath } from '../../utils/worldFormat';
import { DataStatus } from './DataStatus';
import { EntityThumb } from './EntityThumb';
import type { Bag, WorldData } from '../../types/world';

/** "Dónde conseguirlo" → cajas y bolsas. Una bolsa es una lista de resultados posibles, no un paquete. */
export function BagSourceList({
  w,
  sources
}: {
  w: WorldData;
  sources: BagSource[];
}) {
  return <ul className="grid gap-2.5" data-id="element-1121" data-mp="2myh4omsz48n5">
      {sources.map(s => <li key={`${s.bag.id}-${s.levelMin ?? ''}`} className="rounded-xl md:rounded-2xl border border-white/10 bg-[#0A1628] p-3 md:p-4 text-[13px] md:text-base" data-id="element-1122" data-mp="2iu9d8hv4sel8">
          {s.origins.map(o => <p key={o.kind === 'open' ? o.itemKey : o.kind === 'monster' ? `m${o.monsterId}` : o.eventId} className="flex flex-wrap items-center gap-1.5 text-gray-200" data-id="element-1123" data-mp="17f2rl1jecjz7">
              {o.kind === 'open' && <>
                  <EntityThumb entity={{
            type: 'item',
            id: o.itemKey
          }} name={o.itemName} fallback={<Package className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1125" data-mp="1oufsmr53n4yp" />} data-id="element-1124" data-mp="22s4d3xv98a38" />
                  Al abrir
                  <Link to={itemPath(o.itemKey)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1126" data-mp="1sydqkyiou9q6">{o.itemName}</Link>
                </>}
              {o.kind === 'monster' && <>
                  <EntityThumb entity={{
            type: 'monster',
            id: o.monsterId
          }} name={monsterName(w, o.monsterId)} fallback={<Skull className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1128" data-mp="1a6uoqv0eh1pq" />} data-id="element-1127" data-mp="157rkhcs8uzit" />
                  Recompensa al derrotar a
                  <Link to={monsterPath(o.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1129" data-mp="w6tn3dcb9q97">{monsterName(w, o.monsterId)}</Link>
                </>}
              {o.kind === 'event' && <>
                  <CalendarDays className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1130" data-mp="2y2shszx4dpu1" />
                  Premio del evento <Link to={`/guia/eventos#${encodeURIComponent(o.eventId)}`} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1131" data-mp="2t5af3r9p46uh">{o.eventName}</Link>
                </>}
            </p>)}
          <p className="mt-1.5 text-sm text-gray-400" data-id="element-1132" data-mp="3gu20nzss5qya">
            {s.giveAll ? 'Se entrega junto con el resto del contenido.' : s.poolSize ? `Uno de ${s.poolSize} resultados posibles.` : 'Uno de los resultados posibles.'}
            {levelRange(s.levelMin, s.levelMax) && ` Nivel del ítem: ${levelRange(s.levelMin, s.levelMax)}.`}
          </p>
          <p className="mt-1 text-xs text-gray-500" data-id="element-1133" data-mp="21e2yd55ugp5y">Según la configuración exportada: no es un resultado garantizado.</p>
        </li>)}
    </ul>;
}

/** Ficha de una caja: qué puede salir al abrirla. */
export function BoxContents({
  w,
  bags,
  title = 'Qué puede salir al abrirla',
  intro = 'Cada apertura entrega uno de estos resultados posibles, no todos.',
  id = 'contenido'
}: {
  w: WorldData;
  bags: Bag[];
  title?: string;
  intro?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const raw = bags.flatMap(b => bagContents(w, b.id));
  // Resultados retenidos por conflicto entre fuentes: no se presentan como confirmados.
  const all = raw.filter(c => !c.withheld);
  const held = raw.length - all.length;
  if (!raw.length) return null;
  const shown = open ? all : all.slice(0, 12);
  return <section id={id} aria-labelledby={`${id}-t`} className="scroll-mt-20 md:scroll-mt-32" data-id="element-1134" data-mp="14j0mu1592k9g">
      <h2 id={`${id}-t`} className="mb-2.5 md:mb-4 flex items-baseline gap-2 font-cinzel text-lg md:text-2xl font-bold text-white" data-id="element-1135" data-mp="18kcbo4gav1gl">
        {title} <span className="font-sans text-sm font-normal text-gray-500" data-id="element-1136" data-mp="3ad1mxv30e0k0">{all.length}</span>
      </h2>
      <p className="mb-3 text-sm text-gray-400" data-id="element-1137" data-mp="2f2vekhr3dius">{intro}</p>
      {held > 0 && <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-1138" data-mp="2b2bh1d2lwd84">
          <DataStatus kind="conflict" data-id="element-1139" data-mp="2p1szlapb6u91" /> {held} {held === 1 ? 'resultado de la exportación está' : 'resultados de la exportación están'} en revisión por el staff y no se muestran.
        </p>}
      <ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1140" data-mp="2k0wtqrlecy0z">
        {shown.map(c => <li key={`${c.itemKey}-${c.levelMin ?? ''}`} className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0A1628] pl-1.5 pr-3 py-1.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1141" data-mp="1wasranjy34bk">
            {/* Imagen fuera del enlace: tocarla no abre la ficha; el nombre sí. */}
            <EntityThumb entity={{
          type: 'item',
          id: c.itemKey
        }} name={c.name} data-id="element-1142" data-mp="21af9zcijlcrr" />
            <Link to={itemPath(c.itemKey)} className="flex min-w-0 flex-1 items-center justify-between gap-2 py-1 text-sm" data-id="element-1143" data-mp="1dkkusoar3c4c">
              <span className="truncate text-white" data-id="element-1144" data-mp="3kawm7mm1qjiv">{c.name}</span>
              {levelRange(c.levelMin, c.levelMax) && <span className="shrink-0 text-xs text-gray-400" data-id="element-1145" data-mp="3p1czn6pva558">{levelRange(c.levelMin, c.levelMax)}</span>}
            </Link>
          </li>)}
      </ul>
      {all.length > 12 && <button type="button" onClick={() => setOpen(v => !v)} className="mt-2 h-10 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-1146" data-mp="3fbtfzyuby79b">
          {open ? 'Mostrar menos' : `Ver los ${all.length} resultados`}
        </button>}
    </section>;
}